import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import CupNoticeCard from '../components/CupNoticeCard';
import {
  Trophy, RefreshCw, Shuffle, Undo2, Trash2, Plus, Copy, Send, Loader, AlertTriangle, ExternalLink, MessageSquare, Search,
} from 'lucide-react';
import { parseField, parseOdds, oddsTier, sweepSummary, winners, account, live, payRef } from '../utils/cupSweep';

const TIER = { fav: '🔥 Favourite', top: '💪 Top chance', mix: '🙂 In the mix', out: '🤞 Outsider', roughie: '🫏 Roughie' };

// Melbourne Cup sweep 2026 — Paul's side of hollypaul.com/cup.
//
// A PERSON enters with name + mobile and gets one payment reference (CUP-K7Q3M) for all the
// tickets they buy, in any sweep, for themselves or someone they nominate. Paul records the
// money that arrives against the person; it pays their tickets in the order they bought them.
// Only a paid ticket is drawn a horse (server side, crypto.randomInt). Before the race Paul
// texts each person their horses — previewed first, sent only to the people he ticks.
// Backed by cup-sweep-sb (settings KV mcup_2026 + one row per person and per ticket).

const URL = '/.netlify/functions/cup-sweep-sb';
const api = (action, payload = {}) =>
  fetch(URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }) })
    .then((r) => r.json());

const card = { background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 16 };
const th = { textAlign: 'left', padding: '8px 10px', fontSize: '.66rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--hp-dim)', borderBottom: '1px solid var(--hp-border)', whiteSpace: 'nowrap' };
const td = { padding: '8px 10px', fontSize: '.85rem', borderBottom: '1px solid var(--hp-border)', verticalAlign: 'top' };
const label = { display: 'block', fontSize: '.66rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--hp-dim)', marginBottom: 4 };
const dim = { color: 'var(--hp-dim)', fontSize: '.78rem' };
const money = (n) => `$${(Math.round(n * 100) % 100 ? Number(n).toFixed(2) : String(Math.round(n)))}`;
const PLACE = ['1st', '2nd', '3rd'];
const LANG_NAME = { en: 'English', es: 'Español', zh: '中文', mn: 'Монгол' };
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : '');
const sweepShort = (s) => (s.kind === 'team' ? money(s.price) : s.kind === 'family' ? `Family ${money(s.price)}` : `Extended ${money(s.price)}`) + (s.poolNo ? ` #${s.poolNo}` : '');

const PAYID = '0411 424 244';

// The drafts Paul sends. Nothing here goes anywhere on its own: copy them into WhatsApp /
// socials; the team push and the horse texts only go on their Send buttons, after a preview.
const pays = (s) => `1st ${money(s.prizes[0])} · 2nd ${money(s.prizes[1])} · 3rd ${money(s.prizes[2])}`;
const drafts = (link, sweeps) => {
  const by = {}; sweeps.forEach((s) => { by[s.id] = s; });
  const fam = by.fam; const ext = by.ext;
  const famLink = `${link}?s=${fam.key}`; const extLink = `${link}?s=${ext.key}`;
  return {
  pushTitle: '🏇 The race that stops a nation!',
  portal: `Melbourne Cup sweep — Tuesday 3 November, 3pm. Guaranteed prizes in every sweep:
$2 sweep: ${pays(by.s2)}
$5 sweep: ${pays(by.s5)}
$10 sweep: ${pays(by.s10)}
Take as many horses as you like and pay once — cash to Paul or PayID ${PAYID}. Put your own name in, or shout a family member or a mate. First Cup? The page explains it all, in English, Español, 中文 and Монгол.`,
  pushBody: `Melbourne Cup sweep is ON — $10 sweep pays ${money(by.s10.prizes[0])} to the winner! $2, $5 & $10 sweeps, guaranteed prizes. Pick your horses: ${link}`,
  team: `🏇🏆 THE RACE THAT STOPS A NATION! 🏆🏇

Melbourne Cup — Tuesday 3 November, 3:00pm — and the Sydney Parcels sweep is OPEN!

Three sweeps, set prizes — guaranteed:
💵 $2 sweep: ${pays(by.s2)}
💵 $5 sweep: ${pays(by.s5)}
💵 $10 sweep: ${pays(by.s10)}

Take as many horses as you like, in one sweep or across all three, and pay once for the lot. Put your own name in the hat — or shout your mum, partner, kids or a mate a horse.

👉 Enter here: ${link}

First Melbourne Cup? 🇦🇺 The page explains the race and how a sweep works — in English, Español, 中文 and Монгол. No horse knowledge needed, luck picks your horse.

Horses are drawn at random once the final field is out, and I’ll text you yours before the race. May the best horse win! 🐎`,
  payment: `💰 Melbourne Cup sweep — how to pay

When you enter at ${link} you pick your horses and get your own payment reference (it looks like CUP-K7Q3M). One payment covers all your horses, in every sweep.

Two ways to pay:
1️⃣ Cash — hand it to me at the depot and tell me your reference.
2️⃣ PayID — mobile ${PAYID}. Put your reference in the description.

Then tap “I’ve paid” on the page. I check every payment myself, and once I’ve confirmed it, your names go in the draw.

Paul`,
  family: `🏇 Family Melbourne Cup sweep! 🏠

The race that stops a nation is Tuesday 3 November at 3pm, and we’re running a family sweep: ${money(fam.price)} a horse, and it pays ${pays(fam)} — guaranteed.${ext.enabled ? `\n\nThere’s also an extended family sweep at ${money(ext.price)} a horse, paying ${pays(ext)}: ${extLink}` : ''}

Enter here (this link is just for family) 👉 ${famLink}

You can put in your own name or the kids’, and pay by PayID (${PAYID}) with the reference the page gives you, or cash. I’ll text you your horse before the race. Good luck! 🍀`,
  publicPost: `🏇 The race that stops a nation is next Tuesday — and you’re invited into our sweep!

The Sydney Parcels Melbourne Cup sweep is open to our team, their families and friends. Set prizes in every sweep, guaranteed:
• $2 sweep: ${pays(by.s2)}
• $5 sweep: ${pays(by.s5)}
• $10 sweep: ${pays(by.s10)}

Take as many horses as you like and pay once. Enter yourself, or nominate a family member or a friend.

New to the Cup? The page explains the race and how a sweep works.

Pay by cash or PayID (${PAYID}) with your own payment reference. Once I’ve confirmed your payment, your name’s in the draw, and I’ll text you your horse before the race.

Enter here 👉 ${link}

Race day: Tuesday 3 November, 3:00pm AEDT. Good luck! 🍀`,
  };
};

export default function MelbourneCupPage() {
  const { addNotification } = useApp();
  const notify = useCallback((msg, type = 'success') => { if (addNotification) addNotification(msg, type); }, [addNotification]);
  const [tab, setTab] = useState('people');
  const [cfg, setCfg] = useState(null);
  const [payers, setPayers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await api('load');
      if (!r.ok) throw new Error(r.error || 'Could not load the sweep.');
      setCfg(r.config); setPayers(r.payers); setTickets(r.tickets);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Every write returns the person and/or all their tickets; fold them in without a full reload.
  const merge = useCallback((r) => {
    if (r.payer) setPayers((l) => (l.some((p) => p.id === r.payer.id) ? l.map((p) => (p.id === r.payer.id ? r.payer : p)) : [...l, r.payer]));
    if (r.tickets && r.tickets.length) {
      const pid = r.tickets[0].payerId;
      setTickets((l) => [...l.filter((t) => t.payerId !== pid), ...r.tickets]);
    }
  }, []);

  const call = useCallback(async (action, payload, okMsg, key = action) => {
    setBusy(key);
    const r = await api(action, payload).catch(() => ({ ok: false, error: 'No connection.' }));
    setBusy('');
    if (!r.ok) { notify(r.error || 'Could not save.', 'error'); return null; }
    merge(r);
    if (okMsg) notify(typeof okMsg === 'function' ? okMsg(r) : okMsg);
    return r;
  }, [merge, notify]);

  const saveCfg = async (payload, okMsg) => {
    const r = await call('saveConfig', payload, null, 'cfg');
    if (!r) return false;
    notify(okMsg + (r.freed ? ` · ${r.freed} ticket${r.freed === 1 ? '' : 's'} back in the hat — Draw again to give them a new horse` : ''));
    await load();
    return true;
  };

  const origin = window.location.origin;
  const link = `${origin}/cup`;
  const claims = live(payers).filter((p) => p.claimedPaidAt && account(p, tickets).balance > 0).length;

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Trophy size={22} style={{ color: 'var(--hp-gold)' }} /> Melbourne Cup Sweep
          </h2>
          <p>
            Tuesday 3 November 2026, 3:00pm · entry page <a href="/cup" target="_blank" rel="noreferrer" style={{ color: 'var(--hp-gold)' }}>{link.replace(/^https?:\/\//, '')} <ExternalLink size={11} /></a>
          </p>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={load} title="Refresh"><RefreshCw size={14} /></button>
      </div>

      <div className="page-body">
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
          {[['people', `People & payments${claims ? ` · ${claims} to confirm` : ''}`], ['sweeps', 'Sweeps & draw'], ['field', 'Field'], ['results', 'Results'], ['messages', 'Messages & texts']].map(([k, l]) => (
            <button key={k} className={`btn btn-sm ${tab === k ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab(k)}>{l}</button>
          ))}
        </div>

        {err && (
          <div style={{ ...card, borderColor: 'var(--hp-red)', color: 'var(--hp-red)', marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
            <AlertTriangle size={16} /> {err}
          </div>
        )}
        {loading && !cfg && <div style={{ padding: 40, textAlign: 'center', color: 'var(--hp-dim)' }}><Loader size={18} /> Loading…</div>}

        {cfg && tab === 'people' && <PeopleTab cfg={cfg} payers={payers} tickets={tickets} busy={busy} call={call} notify={notify} />}
        {cfg && tab === 'sweeps' && <SweepsTab cfg={cfg} payers={payers} tickets={tickets} busy={busy} setBusy={setBusy} call={call} saveCfg={saveCfg} reload={load} notify={notify} origin={origin} />}
        {cfg && tab === 'field' && <FieldTab cfg={cfg} tickets={tickets} saveCfg={saveCfg} busy={busy} />}
        {cfg && tab === 'results' && <ResultsTab cfg={cfg} payers={payers} tickets={tickets} saveCfg={saveCfg} busy={busy} />}
        {cfg && tab === 'messages' && <MessagesTab cfg={cfg} link={link} notify={notify} />}
      </div>
    </div>
  );
}

// ── People & payments ─────────────────────────────────────────────────────────
function PeopleTab({ cfg, payers, tickets, busy, call, notify }) {
  const [show, setShow] = useState('all');
  const [q, setQ] = useState('');
  const [add, setAdd] = useState({ name: '', mobile: '', lang: 'en' });
  const sweepOf = useMemo(() => { const m = {}; cfg.sweeps.forEach((s) => { m[s.id] = s; }); return m; }, [cfg.sweeps]);
  const horseName = useMemo(() => { const m = {}; cfg.field.forEach((h) => { m[h.no] = h.name; }); return m; }, [cfg.field]);

  const rows = useMemo(() => payers.map((p) => ({ p, acc: account(p, tickets) })), [payers, tickets]);
  const totals = useMemo(() => {
    const l = rows.filter((r) => !r.p.deletedAt);
    return {
      people: l.length, tickets: l.reduce((a, r) => a + r.acc.tickets, 0),
      due: l.reduce((a, r) => a + r.acc.due, 0), received: l.reduce((a, r) => a + r.acc.received, 0),
      owing: l.reduce((a, r) => a + r.acc.balance, 0), credit: l.reduce((a, r) => a + r.acc.credit, 0),
    };
  }, [rows]);

  const needle = q.trim().toLowerCase();
  const shown = rows
    .filter((r) => (show === 'removed' ? r.p.deletedAt : !r.p.deletedAt))
    .filter((r) => (show === 'claims' ? r.p.claimedPaidAt && r.acc.balance > 0 : show === 'owing' ? r.acc.balance > 0 : show === 'paid' ? r.acc.tickets && !r.acc.balance : true))
    .filter((r) => !needle || [r.p.name, payRef(r.p), r.p.mobile].concat(live(tickets).filter((t) => t.payerId === r.p.id).map((t) => t.name))
      .some((v) => String(v || '').toLowerCase().includes(needle)))
    // Says-paid first (Paul's to-do), then owing, then paid up; oldest first within each.
    .sort((a, b) => {
      const rank = (r) => (r.p.claimedPaidAt && r.acc.balance > 0 ? 0 : r.acc.balance > 0 ? 1 : 2);
      return rank(a) - rank(b) || String(a.p.createdAt).localeCompare(String(b.p.createdAt));
    });

  const addPerson = async () => {
    if (add.name.trim().length < 2) return;
    const r = await call('addPayer', add, (x) => `${x.payer.name} added · ${payRef(x.payer)} — now add their tickets`, 'addPayer');
    if (r) setAdd({ name: '', mobile: '', lang: add.lang });
  };

  const stat = (l, v, c) => (
    <div style={{ ...card, padding: '10px 14px', flex: '1 1 120px' }}>
      <div style={label}>{l}</div><b style={{ fontSize: '1.2rem', color: c || 'var(--hp-text)' }}>{v}</b>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        {stat('People', totals.people)}
        {stat('Tickets', totals.tickets)}
        {stat('Due', money(totals.due))}
        {stat('Received', money(totals.received), 'var(--hp-green)')}
        {stat('Still owing', money(totals.owing), totals.owing ? 'var(--hp-amber)' : undefined)}
        {totals.credit > 0 && stat('Overpaid (credit)', money(totals.credit), 'var(--hp-blue)')}
      </div>

      <div style={{ ...card, marginBottom: 14, display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 180px' }}>
          <span style={label}>Add a person who paid you directly — name</span>
          <input className="input" value={add.name} onChange={(e) => setAdd({ ...add, name: e.target.value })} />
        </div>
        <div style={{ flex: '1 1 150px' }}>
          <span style={label}>Mobile (for their horse text)</span>
          <input className="input" value={add.mobile} inputMode="tel" onChange={(e) => setAdd({ ...add, mobile: e.target.value })} placeholder="04xx xxx xxx" />
        </div>
        <div>
          <span style={label}>Text them in</span>
          <select className="input" style={{ width: 'auto' }} value={add.lang} onChange={(e) => setAdd({ ...add, lang: e.target.value })}>
            {Object.keys(LANG_NAME).map((l) => <option key={l} value={l}>{LANG_NAME[l]}</option>)}
          </select>
        </div>
        <button className="btn btn-primary btn-sm" disabled={busy === 'addPayer' || add.name.trim().length < 2} onClick={addPerson}><Plus size={13} /> Add person</button>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
        {[['all', 'All'], ['claims', 'Says paid'], ['owing', 'Owing'], ['paid', 'Paid up'], ['removed', 'Removed']].map(([k, l]) => (
          <button key={k} className={`btn btn-sm ${show === k ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setShow(k)}>{l}</button>
        ))}
        <div style={{ flex: 1 }} />
        <div style={{ position: 'relative', flex: '0 1 260px' }}>
          <Search size={13} style={{ position: 'absolute', left: 9, top: 10, color: 'var(--hp-dim)' }} />
          <input className="input" style={{ paddingLeft: 28 }} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, CUP- reference, mobile" />
        </div>
      </div>

      {!shown.length ? <div style={{ ...card, color: 'var(--hp-dim)', fontSize: '.85rem' }}>Nobody here yet.</div>
        : shown.map(({ p, acc }) => (
          <PersonCard key={p.id} p={p} acc={acc} tickets={tickets.filter((t) => t.payerId === p.id)} cfg={cfg}
            sweepOf={sweepOf} horseName={horseName} busy={busy} call={call} notify={notify} />
        ))}
    </div>
  );
}

function PersonCard({ p, acc, tickets, cfg, sweepOf, horseName, busy, call, notify }) {
  const [pay, setPay] = useState(null); // {amount, method, note} while recording
  const [tk, setTk] = useState(null);   // {sweep, name, relation} while adding a ticket
  const [showRemoved, setShowRemoved] = useState(false);
  const [who, setWho] = useState(null); // {name, mobile, lang} while correcting their details
  const mine = tickets.filter((t) => (showRemoved ? true : !t.deletedAt)).sort((a, b) => String(a.createdAt).localeCompare(String(b.createdAt)));
  const removedCount = tickets.filter((t) => t.deletedAt && !t.deletedWithPayer).length;
  const claim = p.claimedPaidAt && acc.balance > 0;
  const k = (s) => `${s}-${p.id}`;

  const record = async () => {
    const r = await call('recordPayment', { payerId: p.id, amount: pay.amount, method: pay.method, note: pay.note },
      (x) => `${p.name}: ${money(Number(pay.amount))} recorded · ${x.account.balance ? `${money(x.account.balance)} still owing` : 'paid up'}`, k('pay'));
    if (r) setPay(null);
  };
  const addTicket = async () => {
    const r = await call('officeAddTicket', { payerId: p.id, ...tk }, (x) => `${x.added} horse${x.added === 1 ? '' : 's'} added`, k('tk'));
    if (r) setTk(null);
  };

  return (
    <div style={{ ...card, marginBottom: 10, borderColor: claim ? 'var(--hp-amber)' : 'var(--hp-border)', opacity: p.deletedAt ? 0.6 : 1 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 220px' }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 800, color: 'var(--hp-gold)', letterSpacing: '.03em' }}>{payRef(p)}</div>
          {who ? (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 4 }}>
              <div style={{ flex: '1 1 150px' }}><span style={label}>Name</span><input className="input" value={who.name} onChange={(e) => setWho({ ...who, name: e.target.value })} /></div>
              <div style={{ flex: '1 1 130px' }}><span style={label}>Mobile</span><input className="input" inputMode="tel" value={who.mobile} onChange={(e) => setWho({ ...who, mobile: e.target.value })} placeholder="04xx xxx xxx" /></div>
              <div><span style={label}>Text them in</span>
                <select className="input" style={{ width: 'auto' }} value={who.lang} onChange={(e) => setWho({ ...who, lang: e.target.value })}>
                  {Object.keys(LANG_NAME).map((l) => <option key={l} value={l}>{LANG_NAME[l]}</option>)}
                </select></div>
              <button className="btn btn-primary btn-sm" disabled={busy === k('who') || who.name.trim().length < 2}
                onClick={async () => { if (await call('updatePayer', { id: p.id, patch: who }, 'Details saved', k('who'))) setWho(null); }}>Save</button>
              <button className="btn btn-secondary btn-sm" onClick={() => setWho(null)}>Cancel</button>
            </div>
          ) : (
            <>
              <div style={{ fontWeight: 800, fontSize: '1rem' }}>
                {p.name}{' '}
                {!p.deletedAt && <button className="btn btn-secondary btn-sm" style={{ padding: '1px 8px', fontSize: '.7rem' }} onClick={() => setWho({ name: p.name, mobile: p.mobile || '', lang: p.lang || 'en' })}>Edit</button>}
                {!p.deletedAt && p.view && (
                  <>
                    {' '}
                    <button className="btn btn-secondary btn-sm" style={{ padding: '1px 8px', fontSize: '.7rem' }}
                      title="Their private page: every sweep they're in and every horse they drew"
                      onClick={() => navigator.clipboard.writeText(`${window.location.origin}/cup?me=${p.view}`).then(() => notify(`${p.name}'s My horses link copied — paste it into WhatsApp`), () => notify('Could not copy', 'error'))}>
                      <Copy size={10} /> My horses link
                    </button>{' '}
                    <a href={`/cup?me=${p.view}`} target="_blank" rel="noreferrer" style={{ fontSize: '.72rem', color: 'var(--hp-gold)' }}>open <ExternalLink size={10} /></a>
                  </>
                )}
              </div>
              <div style={dim}>
                {p.mobile || <span style={{ color: 'var(--hp-amber)' }}>no mobile — can’t text their horses</span>}
                {' · '}texts in {LANG_NAME[p.lang] || 'English'}{' · '}{p.source === 'office' ? 'added by office' : 'entered online'} {fmtWhen(p.createdAt)}
              </div>
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: 14, textAlign: 'right' }}>
          <div><div style={label}>Due</div><b>{money(acc.due)}</b></div>
          <div><div style={label}>Paid</div><b style={{ color: 'var(--hp-green)' }}>{money(acc.received)}</b></div>
          <div><div style={label}>Owing</div><b style={{ color: acc.balance ? 'var(--hp-amber)' : 'var(--hp-dim)' }}>{money(acc.balance)}</b></div>
          {acc.credit > 0 && <div><div style={label}>Credit</div><b style={{ color: 'var(--hp-blue)' }}>{money(acc.credit)}</b></div>}
        </div>
      </div>

      {claim && (
        <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 8, background: 'rgba(245,158,11,.1)', color: 'var(--hp-amber)', fontSize: '.82rem', fontWeight: 700 }}>
          Says they paid {money(p.claimAmount || acc.balance)} by {p.claimMethod === 'cash' ? 'cash' : 'PayID'} · {fmtWhen(p.claimedPaidAt)} — check for {payRef(p)}, then record it.
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 10 }}>
        <thead><tr><th style={th}>Sweep</th><th style={th}>Name in the hat</th><th style={th}>Price</th><th style={th}>Paid</th><th style={th}>Horse</th><th style={th} /></tr></thead>
        <tbody>
          {!mine.length && <tr><td style={{ ...td, color: 'var(--hp-dim)' }} colSpan={6}>No tickets yet.</td></tr>}
          {mine.map((t) => {
            const s = sweepOf[t.sweep];
            return (
              <tr key={t.id} style={{ opacity: t.deletedAt ? 0.5 : 1 }}>
                <td style={td}>{s ? sweepShort(s) : t.sweep}</td>
                <td style={td}><b>{t.name}</b>{t.nominatedBy ? <div style={dim}>nominated by {t.nominatedBy}{t.relation ? ` (${t.relation})` : ''}</div> : null}</td>
                <td style={td}>{money(t.price)}</td>
                <td style={td}>{t.deletedAt ? <span style={dim}>removed</span> : t.paid ? <span style={{ color: 'var(--hp-green)', fontWeight: 700 }}>✓</span> : <span style={{ color: 'var(--hp-dim)' }}>—</span>}</td>
                <td style={td}>{t.horse != null ? <b>#{t.horse} {horseName[t.horse] || ''}</b>
                  : t.paid ? <span style={dim}>in the hat{t.scratchedFrom ? ` (had #${t.scratchedFrom}, scratched)` : ''}</span> : '—'}</td>
                <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {t.deletedAt
                    ? !t.deletedWithPayer && <button className="btn btn-secondary btn-sm" title="Restore ticket" disabled={!!busy} onClick={() => call('updateTicket', { id: t.id, patch: { deleted: false } }, 'Ticket restored', k('t'))}><Undo2 size={12} /></button>
                    : <button className="btn btn-secondary btn-sm" title="Remove ticket (can be restored)" disabled={!!busy}
                      onClick={() => { if (window.confirm(`Remove ${t.name}'s ${s ? sweepShort(s) : ''} ticket?${t.horse != null ? ` Horse #${t.horse} goes back in the hat.` : ''} Money already paid stays on ${payRef(p)} as credit.`)) call('updateTicket', { id: t.id, patch: { deleted: true } }, 'Ticket removed — restore it from this card', k('t')); }}>
                      <Trash2 size={12} /></button>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {(p.payments || []).length > 0 && (
        <div style={{ marginTop: 10, fontSize: '.8rem' }}>
          <span style={label}>Payments received</span>
          {p.payments.map((x) => (
            <div key={x.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '3px 0', textDecoration: x.deletedAt ? 'line-through' : 'none', color: x.deletedAt ? 'var(--hp-dim)' : 'var(--hp-text)' }}>
              <b>{money(x.amount)}</b> <span>{x.method === 'cash' ? 'Cash' : 'PayID'}</span> <span style={dim}>{fmtWhen(x.at)} · {x.by}{x.note ? ` · ${x.note}` : ''}</span>
              <button className="btn btn-secondary btn-sm" style={{ padding: '1px 8px', fontSize: '.72rem' }} disabled={!!busy}
                onClick={() => { if (x.deletedAt || window.confirm(`Void the ${money(x.amount)} payment? Tickets it paid for go back to unpaid and lose their horse.`)) call('voidPayment', { payerId: p.id, paymentId: x.id, restore: !!x.deletedAt }, x.deletedAt ? 'Payment restored' : 'Payment voided', k('void')); }}>
                {x.deletedAt ? 'Restore' : 'Void'}
              </button>
            </div>
          ))}
        </div>
      )}

      {!p.deletedAt && (
        <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {pay ? (
            <>
              <div><span style={label}>Amount received</span><input className="input" type="number" min="0" step="0.5" style={{ width: 110 }} value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} autoFocus /></div>
              <div><span style={label}>How</span>
                <select className="input" style={{ width: 'auto' }} value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })}>
                  <option value="payid">PayID</option><option value="cash">Cash</option>
                </select></div>
              <div style={{ flex: '1 1 140px' }}><span style={label}>Note (optional)</span><input className="input" value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} placeholder="e.g. paid by their partner" /></div>
              <button className="btn btn-primary btn-sm" disabled={busy === k('pay') || !(Number(pay.amount) > 0)} onClick={record}>Record</button>
              <button className="btn btn-secondary btn-sm" onClick={() => setPay(null)}>Cancel</button>
            </>
          ) : tk ? (
            <>
              <div><span style={label}>Sweep</span>
                <select className="input" style={{ width: 'auto' }} value={tk.sweep} onChange={(e) => setTk({ ...tk, sweep: e.target.value })}>
                  {cfg.sweeps.filter((s) => s.enabled).map((s) => <option key={s.id} value={s.id}>{sweepShort(s)}</option>)}
                </select></div>
              <div><span style={label}>How many</span>
                <input className="input" type="number" min="1" max="10" style={{ width: 70 }} value={tk.qty} onChange={(e) => setTk({ ...tk, qty: e.target.value })} /></div>
              <div style={{ flex: '1 1 160px' }}><span style={label}>Name in the hat</span><input className="input" value={tk.name} onChange={(e) => setTk({ ...tk, name: e.target.value })} placeholder={p.name} /></div>
              <div style={{ flex: '1 1 120px' }}><span style={label}>Relation (optional)</span><input className="input" value={tk.relation} onChange={(e) => setTk({ ...tk, relation: e.target.value })} /></div>
              <button className="btn btn-primary btn-sm" disabled={busy === k('tk') || !(Number(tk.qty) >= 1 && Number(tk.qty) <= 10)} onClick={addTicket}>
                {Number(tk.qty) > 1 ? `Add ${tk.qty} horses` : 'Add horse'}{(() => { const sw = cfg.sweeps.find((x) => x.id === tk.sweep); return sw ? ` · ${money(sw.price * (Number(tk.qty) || 1))}` : ''; })()}</button>
              <span style={{ ...dim, flexBasis: '100%' }}>Different sweeps? Add them one sweep at a time — it’s all on {payRef(p)}, one total.</span>
              <button className="btn btn-secondary btn-sm" onClick={() => setTk(null)}>Cancel</button>
            </>
          ) : (
            <>
              <button className="btn btn-primary btn-sm" onClick={() => setPay({ amount: acc.balance || '', method: p.claimMethod === 'cash' ? 'cash' : 'payid', note: '' })}>Record payment{acc.balance ? ` · ${money(acc.balance)}` : ''}</button>
              <button className="btn btn-secondary btn-sm" onClick={() => setTk({ sweep: 's5', qty: 1, name: '', relation: '' })}><Plus size={13} /> Horses</button>
              {claim && <button className="btn btn-secondary btn-sm" disabled={!!busy} onClick={() => call('updatePayer', { id: p.id, patch: { claimCleared: true } }, 'Flag cleared — not received', k('clr'))}>Not received</button>}
              {removedCount > 0 && <button className="btn btn-secondary btn-sm" onClick={() => setShowRemoved(!showRemoved)}>{showRemoved ? 'Hide' : 'Show'} removed ({removedCount})</button>}
              <div style={{ flex: 1 }} />
              <button className="btn btn-secondary btn-sm" disabled={!!busy}
                onClick={() => { if (window.confirm(`Remove ${p.name} (${payRef(p)}) and all their tickets? You can restore them under Removed.`)) call('updatePayer', { id: p.id, patch: { deleted: true } }, `${p.name} removed`, k('del')); }}>
                <Trash2 size={13} /> Remove person</button>
            </>
          )}
        </div>
      )}
      {p.deletedAt && (
        <div style={{ marginTop: 10 }}>
          <button className="btn btn-secondary btn-sm" disabled={!!busy} onClick={() => call('updatePayer', { id: p.id, patch: { deleted: false } }, `${p.name} restored`, k('del'))}><Undo2 size={13} /> Restore person and tickets</button>
        </div>
      )}
    </div>
  );
}

// ── Sweeps & draw ─────────────────────────────────────────────────────────────
function SweepsTab({ cfg, payers, tickets, busy, setBusy, call, saveCfg, reload, notify, origin }) {
  const [edit, setEdit] = useState({}); // id → {price, split}
  const [sel, setSel] = useState('s2');
  const payerOf = useMemo(() => { const m = {}; payers.forEach((p) => { m[p.id] = p; }); return m; }, [payers]);
  const horseName = useMemo(() => { const m = {}; cfg.field.forEach((h) => { m[h.no] = h.name; }); return m; }, [cfg.field]);

  const doDraw = async (s) => {
    const r = await call('draw', { sweep: s.id }, null, 'draw');
    if (!r) return;
    notify(r.drawn ? `${sweepShort(s)}: drew ${r.drawn} horse${r.drawn === 1 ? '' : 's'}${r.left ? ` · ${r.left} ticket${r.left === 1 ? '' : 's'} left with no horse — the hat is empty` : ''}` : 'Nobody waiting for a horse.');
    reload();
  };

  const selSweep = cfg.sweeps.find((s) => s.id === sel);
  const rows = live(tickets).filter((t) => t.sweep === sel).sort((a, b) => (b.paid - a.paid) || (a.horse ?? 999) - (b.horse ?? 999));

  // Tidy-up before a draw: tick tickets, then Move them to another pool of the same price, or
  // Swap each for smaller tickets of the same money ($10 → 2 × $5). Drawn tickets can't be ticked.
  const [picked, setPicked] = useState({});
  const [moveTo, setMoveTo] = useState('');
  const [swapTo, setSwapTo] = useState('');
  useEffect(() => { setPicked({}); setMoveTo(''); setSwapTo(''); }, [sel]);
  const pickedIds = Object.keys(picked).filter((id) => picked[id] && rows.some((t) => t.id === id && t.horse == null));
  const runners = cfg.field.filter((h) => !h.scratched).length || 24;
  const paidHere = rows.filter((t) => t.paid).length;
  const over = paidHere - runners;
  // Best first: the pool with the most room; for a swap, the next price down ($10 → $5 before $2).
  const roomIn = (x) => runners - live(tickets).filter((t) => t.sweep === x.id && t.paid).length;
  const moveTargets = selSweep ? cfg.sweeps.filter((x) => x.enabled && x.id !== selSweep.id && x.price === selSweep.price)
    .sort((x, y) => roomIn(y) - roomIn(x)) : [];
  const swapTargets = selSweep ? cfg.sweeps.filter((x) => x.enabled && x.price < selSweep.price && selSweep.price % x.price === 0)
    .sort((x, y) => (y.price - x.price) || (roomIn(y) - roomIn(x))) : [];
  const runMove = async (action, to) => {
    const dest = cfg.sweeps.find((x) => x.id === to);
    const each = action === 'swapTickets' ? selSweep.price / dest.price : 1;
    const what = action === 'swapTickets'
      ? `Swap ${pickedIds.length} ${money(selSweep.price)} ticket${pickedIds.length === 1 ? '' : 's'} for ${pickedIds.length * each} × ${money(dest.price)} horses in ${dest.label}?`
      : `Move ${pickedIds.length} ticket${pickedIds.length === 1 ? '' : 's'} to ${dest.label}?`;
    if (!window.confirm(`${what} Same people, same names, same money — nothing changes with their payment.`)) return;
    setBusy('move');
    const r = await api(action, { ids: pickedIds, to }).catch(() => ({ ok: false, error: 'No connection.' }));
    setBusy('');
    if (!r.ok) { notify(r.error || 'Could not move them.', 'error'); return; }
    notify(action === 'swapTickets' ? `${r.moved} swapped into ${r.to}` : `${r.moved} moved to ${r.to}`);
    setPicked({});
    reload();
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 12, marginBottom: 16 }}>
        {cfg.sweeps.map((s) => {
          const sum = sweepSummary(s, tickets, cfg.field);
          // Prizes are fixed dollars Paul guarantees; collected vs promised shows what he covers or keeps.
          const e = edit[s.id] || { price: s.price, prizes: s.prizes };
          const dirty = Number(e.price) !== s.price || e.prizes.map(Number).join() !== s.prizes.join();
          const valid = e.prizes.every((n) => n !== '' && Number(n) >= 0) && Number(e.price) > 0;
          const promised = s.prizes.reduce((a, n) => a + n, 0);
          const gap = sum.pot - promised;
          const priv = s.kind !== 'team';
          const linkUrl = priv ? `${origin}/cup?s=${s.key}` : `${origin}/cup`;
          return (
            <div key={s.id} style={{ ...card, borderColor: sel === s.id ? 'var(--hp-gold)' : 'var(--hp-border)', opacity: s.enabled ? 1 : 0.7 }} onClick={() => setSel(s.id)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                <b style={{ fontSize: priv ? '1.05rem' : '1.4rem' }}>{priv ? s.label : money(s.price)}{!priv && s.poolNo ? <span style={{ fontSize: '.9rem', color: 'var(--hp-gold)' }}> pool #{s.poolNo}</span> : null}</b>
                <span style={{ fontSize: '.75rem', color: !s.enabled ? 'var(--hp-dim)' : s.open ? 'var(--hp-green)' : 'var(--hp-red)' }}>{!s.enabled ? 'Off' : s.open ? 'Open' : 'Closed'}</span>
              </div>
              {priv && (
                <div style={{ ...dim, margin: '4px 0', wordBreak: 'break-all' }} onClick={(ev) => ev.stopPropagation()}>
                  Private link: <a href={linkUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--hp-gold)' }}>{linkUrl.replace(/^https?:\/\//, '')}</a>{' '}
                  <button className="btn btn-secondary btn-sm" style={{ padding: '1px 8px', fontSize: '.7rem' }} title="Copy link" onClick={() => navigator.clipboard.writeText(linkUrl).then(() => notify('Link copied'))}><Copy size={11} /></button>
                </div>
              )}
              <div style={{ ...dim, margin: '4px 0 10px' }}>
                {sum.paid} paid · {sum.entries - sum.paid} unpaid{sum.runners ? ` · ${sum.spotsLeft} of ${sum.runners} horses left` : ''}
              </div>
              <div style={{ fontSize: '.85rem', marginBottom: 8 }}>
                Collected <b>{money(sum.pot)}</b> · prizes <b>{money(promised)}</b>
                <div style={{ fontSize: '.75rem', color: gap < 0 ? 'var(--hp-amber)' : 'var(--hp-green)' }}>
                  {gap < 0 ? `You cover ${money(-gap)} if it stays like this` : `${money(gap)} over the prizes`}
                  {s.price ? ` · sold out (24) collects ${money(24 * s.price)}` : ''}
                </div>
              </div>
              <div onClick={(ev) => ev.stopPropagation()}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.82rem', marginBottom: 4 }}>
                  <span style={{ width: 30, color: 'var(--hp-dim)' }}>Price</span>
                  <span style={{ color: 'var(--hp-dim)' }}>$</span>
                  <input className="input" type="number" min="1" value={e.price} style={{ width: 72, padding: '4px 6px' }}
                    onChange={(ev) => setEdit({ ...edit, [s.id]: { ...e, price: ev.target.value } })} />
                </div>
                <div style={{ ...label, marginTop: 8 }}>Prizes (guaranteed)</div>
                {[0, 1, 2].map((i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.82rem', padding: '2px 0' }}>
                    <span style={{ width: 30, color: 'var(--hp-dim)' }}>{PLACE[i]}</span>
                    <span style={{ color: 'var(--hp-dim)' }}>$</span>
                    <input className="input" type="number" min="0" value={e.prizes[i]} style={{ width: 84, padding: '4px 6px' }}
                      onChange={(ev) => setEdit({ ...edit, [s.id]: { ...e, prizes: e.prizes.map((v, j) => (j === i ? ev.target.value : v)) } })} />
                  </div>
                ))}
                {dirty && (
                  <div style={{ marginTop: 6 }}>
                    {Number(e.price) !== s.price && sum.entries > 0 && <div style={{ fontSize: '.74rem', color: 'var(--hp-amber)', marginBottom: 4 }}>Tickets already bought keep the {money(s.price)} they were sold at.</div>}
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <button className="btn btn-primary btn-sm" disabled={!valid || busy === 'cfg'}
                        onClick={async () => { if (await saveCfg({ sweeps: [{ id: s.id, price: Number(e.price), prizes: e.prizes.map(Number) }] }, `${s.label}: saved — the page, messages and texts show the new prizes`)) setEdit({ ...edit, [s.id]: undefined }); }}>Save</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => setEdit({ ...edit, [s.id]: undefined })}>Cancel</button>
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                  {s.enabled && (
                    <button className="btn btn-primary btn-sm" disabled={busy === 'draw' || !sum.waiting || !sum.runners}
                      title={!sum.runners ? 'Load the field first' : ''} onClick={() => doDraw(s)}>
                      <Shuffle size={13} /> Draw {sum.waiting ? `${sum.waiting} horse${sum.waiting === 1 ? '' : 's'}` : 'horses'}
                    </button>
                  )}
                  {s.enabled && (
                    <button className="btn btn-secondary btn-sm" disabled={busy === 'cfg'}
                      onClick={() => saveCfg({ sweeps: [{ id: s.id, open: !s.open }] }, `${s.label} ${s.open ? 'closed to new tickets' : 'reopened'}`)}>
                      {s.open ? 'Close entries' : 'Reopen'}
                    </button>
                  )}
                  {!s.poolOf && s.enabled && (
                    <button className="btn btn-secondary btn-sm" disabled={busy === 'addPool'}
                      title={`Another ${sweepShort({ ...s, poolNo: null })} sweep with its own 24 horses, prizes and draw`}
                      onClick={async () => {
                        if (!window.confirm(`Open another ${sweepShort({ ...s, poolNo: null })} sweep? It gets its own 24 horses and its own prizes (copied from this one, editable). Punters see it straight away${priv ? ' (on the private link)' : ''}.`)) return;
                        const r = await call('addPool', { base: s.id }, (x) => `${x.config.sweeps.find((y) => y.id === x.added).label} is open`, 'addPool');
                        if (r) reload();
                      }}>
                      <Plus size={13} /> Another pool
                    </button>
                  )}
                  {(priv || s.poolOf) && (
                    <button className="btn btn-secondary btn-sm" disabled={busy === 'cfg' || (s.enabled && sum.entries > 0)}
                      title={s.enabled && sum.entries > 0 ? 'Has tickets — close entries instead' : ''}
                      onClick={() => saveCfg({ sweeps: [{ id: s.id, enabled: !s.enabled }] }, `${s.label} switched ${s.enabled ? 'off' : 'on'}`)}>
                      {s.enabled ? 'Switch off' : 'Switch on'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={card}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <b>{selSweep ? selSweep.label : ''} tickets</b>
          <span style={{ fontSize: '.82rem', fontWeight: 700, color: over > 0 ? 'var(--hp-red)' : paidHere === runners ? 'var(--hp-green)' : 'var(--hp-dim)' }}>
            {paidHere} paid of {runners} horses{over > 0 ? ` — ${over} too many: tick ${over} and move them before you draw` : paidHere === runners ? ' — full' : ` — ${runners - paidHere} horses left`}
          </span>
        </div>

        {pickedIds.length > 0 && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '10px 0', padding: 10, borderRadius: 8, background: 'var(--hp-surface)', border: '1px solid var(--hp-gold)' }}>
            <b style={{ fontSize: '.85rem' }}>{pickedIds.length} ticked</b>
            {moveTargets.length > 0 && (
              <>
                <select className="input" style={{ width: 'auto' }} value={moveTo || moveTargets[0].id} onChange={(e) => setMoveTo(e.target.value)}>
                  {moveTargets.map((x) => <option key={x.id} value={x.id}>{x.label} ({roomIn(x)} left)</option>)}
                </select>
                <button className="btn btn-primary btn-sm" disabled={busy === 'move'} onClick={() => runMove('moveTickets', moveTo || moveTargets[0].id)}>Move to this sweep</button>
              </>
            )}
            {swapTargets.length > 0 && (
              <>
                <span style={dim}>or swap each ticket into</span>
                <select className="input" style={{ width: 'auto' }} value={swapTo || swapTargets[0].id} onChange={(e) => setSwapTo(e.target.value)}>
                  {swapTargets.map((x) => <option key={x.id} value={x.id}>{x.label} — {selSweep.price / x.price} horses each ({roomIn(x)} left)</option>)}
                </select>
                <button className="btn btn-primary btn-sm" disabled={busy === 'move'} onClick={() => runMove('swapTickets', swapTo || swapTargets[0].id)}>Swap</button>
              </>
            )}
            {!moveTargets.length && !swapTargets.length && <span style={dim}>Open “Another pool” on this sweep first, so there’s somewhere to move them.</span>}
            <button className="btn btn-secondary btn-sm" onClick={() => setPicked({})}>Clear</button>
          </div>
        )}

        {!rows.length ? <div style={{ ...dim, padding: 8 }}>No tickets yet.</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
              <thead><tr><th style={{ ...th, width: 34 }} /><th style={th}>Name in the hat</th><th style={th}>Person · reference</th><th style={th}>Paid</th><th style={th}>Horse</th></tr></thead>
              <tbody>
                {rows.map((t) => {
                  const p = payerOf[t.payerId];
                  const canPick = t.horse == null;
                  // Ticking one of a person's tickets offers the rest: tick-all for that person.
                  const theirs = rows.filter((x) => x.payerId === t.payerId && x.horse == null);
                  return (
                    <tr key={t.id} style={{ background: picked[t.id] ? 'rgba(203,165,76,.12)' : 'transparent' }}>
                      <td style={td}>
                        <input type="checkbox" disabled={!canPick} title={canPick ? 'Tick to move' : 'Already drawn — can’t move'}
                          checked={!!picked[t.id]} onChange={(e) => setPicked({ ...picked, [t.id]: e.target.checked })} />
                      </td>
                      <td style={td}><b>{t.name}</b>{t.relation ? <div style={dim}>{t.relation}</div> : null}</td>
                      <td style={td}>{p ? p.name : '?'} <span style={{ ...dim, fontFamily: 'var(--font-mono)' }}>{p ? payRef(p) : ''}</span>
                        {theirs.length > 1 && canPick && (
                          <button className="btn btn-secondary btn-sm" style={{ padding: '0 6px', fontSize: '.68rem', marginLeft: 6 }}
                            onClick={() => { const on = !theirs.every((x) => picked[x.id]); const n = { ...picked }; theirs.forEach((x) => { n[x.id] = on; }); setPicked(n); }}>
                            {theirs.every((x) => picked[x.id]) ? 'untick' : 'tick'} all {theirs.length}
                          </button>
                        )}
                      </td>
                      <td style={td}>{t.paid ? <span style={{ color: 'var(--hp-green)', fontWeight: 700 }}>✓</span> : '—'}</td>
                      <td style={td}>{t.horse != null ? <b>#{t.horse} {horseName[t.horse] || ''}</b> : t.paid ? <span style={dim}>in the hat</span> : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Field ─────────────────────────────────────────────────────────────────────
function FieldTab({ cfg, tickets, saveCfg, busy }) {
  const [paste, setPaste] = useState('');
  const [oddsText, setOddsText] = useState('');
  const [oddsSource, setOddsSource] = useState((cfg.odds && cfg.odds.source) || 'Ladbrokes');
  const oddsParsed = useMemo(() => parseOdds(oddsText, cfg.field), [oddsText, cfg.field]);
  const oddsCount = Object.keys(oddsParsed.odds).length;
  const parsed = useMemo(() => parseField(paste), [paste]);
  const held = useMemo(() => {
    const m = {};
    live(tickets).forEach((t) => { if (t.paid && t.horse != null) m[t.horse] = (m[t.horse] || 0) + 1; });
    return m;
  }, [tickets]);

  const replace = () => {
    if (Object.keys(held).length && !window.confirm('Horses are already drawn. Replacing the field keeps a ticket’s horse only if that number is still a runner — any other goes back in the hat. Go ahead?')) return;
    saveCfg({ field: parsed }, `Field saved · ${parsed.length} runners`).then((ok) => { if (ok) setPaste(''); });
  };
  const toggleScratch = (h) => {
    const n = held[h.no] || 0;
    if (!h.scratched && n && !window.confirm(`Scratch #${h.no} ${h.name}? ${n} ticket${n === 1 ? '' : 's'} hold it and go back in the hat — then Draw again on Sweeps & draw.`)) return;
    saveCfg({ field: cfg.field.map((x) => (x.no === h.no ? { ...x, scratched: !x.scratched } : x)) }, `#${h.no} ${h.scratched ? 'reinstated' : 'scratched'}`);
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
      <div style={card}>
        <b>Runners ({cfg.field.filter((h) => !h.scratched).length})</b>
        {!cfg.field.length ? (
          <p style={{ color: 'var(--hp-dim)', fontSize: '.85rem' }}>No field yet. The final field and barriers are announced the Saturday before the race — paste it on the right.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
            <thead><tr><th style={th}>#</th><th style={th}>Horse</th><th style={th}>Barrier</th><th style={th}>Odds</th><th style={th}>Tickets</th><th style={th} /></tr></thead>
            <tbody>
              {cfg.field.map((h) => (
                <tr key={h.no} style={{ opacity: h.scratched ? 0.5 : 1 }}>
                  <td style={td}><b>{h.no}</b></td>
                  <td style={{ ...td, textDecoration: h.scratched ? 'line-through' : 'none' }}>{h.name}</td>
                  <td style={td}>{h.barrier || ''}</td>
                  <td style={td}>{h.odds ? <><b>${h.odds}</b> <span style={dim}>{TIER[oddsTier(h, cfg.field)] || ''}</span></> : ''}</td>
                  <td style={td}>{held[h.no] || ''}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <button className="btn btn-secondary btn-sm" disabled={busy === 'cfg'} onClick={() => toggleScratch(h)}>{h.scratched ? 'Reinstate' : 'Scratch'}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div style={card}>
        <b>Paste the field</b>
        <p style={{ color: 'var(--hp-dim)', fontSize: '.8rem', margin: '4px 0 8px' }}>
          One horse per line, saddlecloth number first: <code>1 Horse Name (12)</code> — the number in brackets is the barrier. Replaces the whole field.
        </p>
        <textarea className="input" rows={12} value={paste} onChange={(e) => setPaste(e.target.value)} style={{ width: '100%', fontFamily: 'var(--font-mono)', fontSize: '.8rem' }}
          placeholder={'1 First Horse (7)\n2 Second Horse (14)\n…'} />
        {parsed.length > 0 && (
          <div style={{ fontSize: '.8rem', color: 'var(--hp-dim)', margin: '8px 0' }}>
            Reads as {parsed.length} runners: {parsed.slice(0, 4).map((h) => `#${h.no} ${h.name}${h.barrier ? ` (b${h.barrier})` : ''}`).join(', ')}{parsed.length > 4 ? '…' : ''}
          </div>
        )}
        <button className="btn btn-primary btn-sm" disabled={!parsed.length || busy === 'cfg'} onClick={replace}>Save field</button>

        <div style={{ borderTop: '1px solid var(--hp-border)', margin: '18px 0 12px' }} />
        <b>Paste the odds</b>
        <p style={{ color: 'var(--hp-dim)', fontSize: '.8rem', margin: '4px 0 8px' }}>
          Punters see each horse’s odds and a tag (🔥 Favourite … 🫏 Roughie) so they know a chance from a donkey.
          One horse per line, odds last: <code>Point Of Law $6.50</code> or <code>20 6.50</code>. Only the horses you paste change.
          {cfg.odds && cfg.odds.asAt ? ` Showing: ${cfg.odds.source}, ${fmtWhen(cfg.odds.asAt)}.` : ''}
        </p>
        <textarea className="input" rows={8} value={oddsText} onChange={(e) => setOddsText(e.target.value)} style={{ width: '100%', fontFamily: 'var(--font-mono)', fontSize: '.8rem' }}
          placeholder={'Point Of Law $6.50\nTrustyourinstinct 8\n12 10.00\n…'} />
        {oddsText.trim() && (
          <div style={{ fontSize: '.8rem', margin: '8px 0', color: oddsParsed.unmatched.length ? 'var(--hp-amber)' : 'var(--hp-dim)' }}>
            Matched {oddsCount} horse{oddsCount === 1 ? '' : 's'}{oddsParsed.unmatched.length ? ` · couldn’t place: ${oddsParsed.unmatched.slice(0, 3).join(' | ')}${oddsParsed.unmatched.length > 3 ? '…' : ''}` : ''}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div><span style={label}>Source</span><input className="input" style={{ width: 160 }} value={oddsSource} onChange={(e) => setOddsSource(e.target.value)} /></div>
          <button className="btn btn-primary btn-sm" disabled={!oddsCount || busy === 'cfg'}
            onClick={() => saveCfg({ odds: oddsParsed.odds, oddsSource }, `Odds saved for ${oddsCount} horses`).then((ok) => { if (ok) setOddsText(''); })}>Save odds</button>
        </div>
      </div>
    </div>
  );
}

// ── Results ───────────────────────────────────────────────────────────────────
function ResultsTab({ cfg, payers, tickets, saveCfg, busy }) {
  const [r, setR] = useState({ first: cfg.results.first || '', second: cfg.results.second || '', third: cfg.results.third || '' });
  const runners = cfg.field.filter((h) => !h.scratched);
  const payerOf = useMemo(() => { const m = {}; payers.forEach((p) => { m[p.id] = p; }); return m; }, [payers]);
  return (
    <div>
      <div style={{ ...card, marginBottom: 16 }}>
        <b>Placings</b>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10, alignItems: 'flex-end' }}>
          {['first', 'second', 'third'].map((k, i) => (
            <div key={k} style={{ flex: '1 1 180px' }}>
              <span style={label}>{PLACE[i]}</span>
              <select className="input" value={r[k]} onChange={(e) => setR({ ...r, [k]: e.target.value })}>
                <option value="">—</option>
                {runners.map((h) => <option key={h.no} value={h.no}>#{h.no} {h.name}</option>)}
              </select>
            </div>
          ))}
          <button className="btn btn-primary btn-sm" disabled={busy === 'cfg'} onClick={() => saveCfg({ results: r }, 'Results saved — they show on the entry page')}>Save results</button>
        </div>
        <p style={{ color: 'var(--hp-dim)', fontSize: '.78rem', margin: '8px 0 0' }}>Saved results show on the public page straight away (first name + initial only).</p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
        {cfg.sweeps.filter((s) => s.enabled).map((s) => {
          const w = winners(s, tickets, cfg.field, cfg.results);
          return (
            <div key={s.id} style={card}>
              <b>{s.label}</b>
              {w.map((x, i) => {
                const p = x && x.ticket ? payerOf[x.ticket.payerId] : null;
                return (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '8px 0', borderBottom: '1px solid var(--hp-border)', fontSize: '.85rem' }}>
                    <span>
                      <span style={{ color: 'var(--hp-dim)' }}>{PLACE[i]}</span>{' '}
                      {x ? <>#{x.horse} {x.horseName} — {x.ticket ? <b>{x.ticket.name}</b> : <span style={{ color: 'var(--hp-amber)' }}>no ticket on this horse</span>}
                        {p && <div style={dim}>{x.ticket.nominatedBy ? `nominated by ${p.name} · ` : ''}{payRef(p)}{p.mobile ? ` · ${p.mobile}` : ''}</div>}</> : '—'}
                    </span>
                    <b>{x ? money(x.prize) : ''}</b>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Messages & texts ──────────────────────────────────────────────────────────
function CopyBox({ title, text, notify }) {
  return (
    <div style={{ ...card, marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
        <b>{title}</b>
        <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }}
          onClick={() => navigator.clipboard.writeText(text).then(() => notify('Copied'), () => notify('Could not copy', 'error'))}><Copy size={13} /> Copy</button>
      </div>
      <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '.85rem', margin: 0, color: 'var(--hp-text)' }}>{text}</pre>
    </div>
  );
}

// Each person's horses, texted in the language they entered in. Preview first; Send goes only
// to the ticked people. Someone already texted starts unticked, so a second press doesn't repeat it.
function HorseTexts({ notify }) {
  const [rows, setRows] = useState(null);
  const [pick, setPick] = useState({});
  const [busy, setBusy] = useState(false);

  const preview = async () => {
    setBusy(true);
    const r = await api('smsPreview').catch(() => ({ ok: false, error: 'No connection.' }));
    setBusy(false);
    if (!r.ok) { notify(r.error || 'Could not build the texts.', 'error'); return; }
    setRows(r.rows);
    const p = {};
    // Ticked: everyone not yet texted, and anyone who has drawn a horse since their last text.
    r.rows.forEach((x) => { p[x.payerId] = !!x.mobile && !!x.changed; });
    setPick(p);
  };
  const ids = rows ? rows.filter((x) => pick[x.payerId]).map((x) => x.payerId) : [];
  const send = async () => {
    if (!window.confirm(`Text ${ids.length} ${ids.length === 1 ? 'person' : 'people'} their horses now?`)) return;
    setBusy(true);
    const r = await api('smsSend', { payerIds: ids }).catch(() => ({ ok: false, error: 'No connection.' }));
    setBusy(false);
    if (!r.ok) { notify(r.error || 'Send failed.', 'error'); return; }
    const okN = r.results.filter((x) => x.ok).length;
    notify(`Texted ${okN} of ${r.results.length}${okN < r.results.length ? ' — the rest failed, try them again' : ''}`, okN < r.results.length ? 'error' : 'success');
    preview();
  };

  return (
    <div style={{ ...card, marginBottom: 12, borderColor: 'var(--hp-gold)' }}>
      <b style={{ display: 'flex', alignItems: 'center', gap: 6 }}><MessageSquare size={15} /> Text everyone their horses</b>
      <p style={{ ...dim, margin: '4px 0 10px' }}>
        One SMS per person from “HPR Engine”, listing every horse their paid tickets drew, in the language they entered in.
        Draw every sweep first. Nothing is sent until you press Send.
      </p>
      <button className="btn btn-secondary btn-sm" disabled={busy} onClick={preview}>{rows ? 'Rebuild preview' : 'Preview the texts'}</button>
      {rows && !rows.length && <div style={{ ...dim, marginTop: 8 }}>Nobody has a drawn horse yet.</div>}
      {rows && rows.length > 0 && (
        <>
          <div style={{ maxHeight: 420, overflowY: 'auto', marginTop: 10 }}>
            {rows.map((x) => (
              <label key={x.payerId} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--hp-border)', cursor: 'pointer' }}>
                <input type="checkbox" checked={!!pick[x.payerId]} disabled={!x.mobile} onChange={(e) => setPick({ ...pick, [x.payerId]: e.target.checked })} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '.85rem' }}><b>{x.name}</b> <span style={{ ...dim, fontFamily: 'var(--font-mono)' }}>{x.ref}</span> <span style={dim}>{x.mobile || 'no mobile'} · {LANG_NAME[x.lang]}</span></div>
                  {(x.waiting > 0 || x.unpaid > 0 || x.balance > 0) && (
                    <div style={{ fontSize: '.75rem', color: 'var(--hp-amber)' }}>
                      {[x.waiting && `${x.waiting} paid ticket${x.waiting === 1 ? '' : 's'} not drawn yet`, x.unpaid && `${x.unpaid} unpaid`, x.balance && `owes ${money(x.balance)}`].filter(Boolean).join(' · ')}
                    </div>
                  )}
                  {x.lastSent && <div style={{ fontSize: '.75rem', color: x.changed ? 'var(--hp-amber)' : 'var(--hp-green)' }}>
                    Texted {fmtWhen(x.lastSent.at)}{x.changed ? ' · new horses since — ticked to send again' : ' · up to date'}</div>}
                  <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '.78rem', margin: '4px 0 0', color: 'var(--hp-dim)' }}>{x.text}</pre>
                </div>
              </label>
            ))}
          </div>
          <button className="btn btn-primary btn-sm" style={{ marginTop: 10 }} disabled={busy || !ids.length} onClick={send}>
            <Send size={13} /> Send to {ids.length} {ids.length === 1 ? 'person' : 'people'}
          </button>
        </>
      )}
    </div>
  );
}

// Post the message into BOTH portals as a card (drivers and sorters), and optionally push it to
// drivers' phones. Sorters can't get pushes, so for them the card is the message.
function PortalNotice({ d, notify }) {
  const [title, setTitle] = useState(d.pushTitle);
  const [body, setBody] = useState(d.portal);
  const [alsoPush, setAlsoPush] = useState(true);
  const [cur, setCur] = useState(undefined);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api('getNotice').then((r) => { if (r.ok) setCur(r.notice); }); }, []);

  const post = async () => {
    const reach = alsoPush ? await api('pushPreview').catch(() => ({ ok: false })) : null;
    const pushLine = alsoPush ? `\n\nAlso push it to ${reach && reach.ok ? reach.recipients : 'the'} drivers with HP Driver alerts on.` : '';
    if (!window.confirm(`Post this at the top of the Driver portal and the Sorter portal?${pushLine}\n\n${title}\n${body}`)) return;
    setBusy(true);
    const r = await api('postNotice', { title, body, push: alsoPush, pushBody: d.pushBody }).catch(() => ({ ok: false, error: 'No connection.' }));
    setBusy(false);
    if (!r.ok) { notify(r.error || 'Could not post.', 'error'); return; }
    setCur(r.notice);
    notify(`Posted in both portals${r.pushed ? ` · pushed to ${r.pushed.reached} of ${r.pushed.recipients} drivers` : ''}`);
  };
  const takeDown = async () => {
    if (!window.confirm('Take the sweep notice out of both portals?')) return;
    const r = await api('clearNotice').catch(() => ({ ok: false }));
    if (r.ok) { setCur(null); notify('Notice taken down'); }
  };

  return (
    <div style={{ ...card, marginBottom: 12, borderColor: 'var(--hp-gold)' }}>
      <b>Post to the Driver &amp; Sorter portals</b>
      <p style={{ ...dim, margin: '4px 0 10px' }}>
        A card at the top of both portals with an “Enter the sweep” button, until the race starts. Sorters see it next time they open their portal (they can’t get pushes); drivers also get a push if ticked. Nothing goes out until you press Post.
      </p>
      {cur && (
        <div style={{ fontSize: '.8rem', color: 'var(--hp-green)', marginBottom: 8 }}>
          Showing now: “{cur.title}” · posted {fmtWhen(cur.postedAt)}{' '}
          <button className="btn btn-secondary btn-sm" style={{ padding: '1px 8px', fontSize: '.72rem' }} onClick={takeDown}>Take down</button>
        </div>
      )}
      <span style={label}>Title</span>
      <input className="input" style={{ width: '100%' }} value={title} maxLength={80} onChange={(e) => setTitle(e.target.value)} />
      <span style={{ ...label, marginTop: 10 }}>Message</span>
      <textarea className="input" rows={6} value={body} maxLength={600} onChange={(e) => setBody(e.target.value)} style={{ width: '100%' }} />
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: '.82rem', margin: '8px 0' }}>
        <input type="checkbox" checked={alsoPush} onChange={(e) => setAlsoPush(e.target.checked)} /> Also push it to drivers’ phones (HP Driver)
      </label>
      <button className="btn btn-primary btn-sm" disabled={busy || !title.trim() || !body.trim()} onClick={post}>
        <Send size={13} /> {cur ? 'Post again (replaces the card)' : 'Post to both portals'}
      </button>
      <span style={{ ...label, marginTop: 14 }}>How it looks in the portals</span>
      <div style={{ maxWidth: 420 }}><CupNoticeCard preview={{ id: 'preview', title, body, link: '/cup' }} /></div>
    </div>
  );
}

function MessagesTab({ cfg, link, notify }) {
  const d = useMemo(() => drafts(link, cfg.sweeps), [link, cfg.sweeps]);
  const [title, setTitle] = useState(d.pushTitle);
  const [body, setBody] = useState(d.pushBody);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);

  const check = async () => {
    setBusy(true);
    const r = await api('pushPreview').catch(() => ({ ok: false }));
    setBusy(false);
    if (!r.ok) { notify(r.error || 'Could not check.', 'error'); return; }
    setPreview(r);
  };
  const send = async () => {
    if (!window.confirm(`Send this push to ${preview.recipients} people on HP Driver?\n\n${title}\n${body}`)) return;
    setBusy(true);
    const r = await api('pushSend', { title, body }).catch(() => ({ ok: false, error: 'No connection.' }));
    setBusy(false);
    if (!r.ok) { notify(r.error || 'Push failed.', 'error'); return; }
    setSent(r);
    notify(`Pushed to ${r.reached} of ${r.recipients} people`);
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16, alignItems: 'start' }}>
      <div>
        <PortalNotice d={d} notify={notify} />
        <HorseTexts notify={notify} />
        <div style={{ ...card, marginBottom: 12 }}>
          <b>Push to the team (HP Driver)</b>
          <p style={{ ...dim, margin: '4px 0 10px' }}>
            Nothing is sent until you press Send. Reaches everyone who has turned on alerts in HP Driver — tapping it opens the entry page.
          </p>
          <span style={label}>Title</span>
          <input className="input" value={title} maxLength={80} onChange={(e) => { setTitle(e.target.value); setSent(null); }} />
          <span style={{ ...label, marginTop: 10 }}>Message</span>
          <textarea className="input" rows={4} value={body} maxLength={300} onChange={(e) => { setBody(e.target.value); setSent(null); }} style={{ width: '100%' }} />
          <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            {!preview ? (
              <button className="btn btn-secondary btn-sm" disabled={busy} onClick={check}>Check who it reaches</button>
            ) : (
              <button className="btn btn-primary btn-sm" disabled={busy || !preview.recipients || !!sent} onClick={send}>
                <Send size={13} /> {sent ? 'Sent' : `Send to ${preview.recipients} people`}
              </button>
            )}
            {preview && !sent && <span style={dim}>{preview.recipients} people have HP Driver alerts on</span>}
            {sent && <span style={{ fontSize: '.78rem', color: 'var(--hp-green)' }}>Reached {sent.reached} of {sent.recipients} ({sent.devices} devices)</span>}
          </div>
        </div>
        <CopyBox title="Payment options — for the team" text={d.payment} notify={notify} />
      </div>
      <div>
        <CopyBox title="Family message (private family link)" text={d.family} notify={notify} />
        <CopyBox title="Team message (WhatsApp)" text={d.team} notify={notify} />
        <CopyBox title="Public post — friends, socials" text={d.publicPost} notify={notify} />
      </div>
    </div>
  );
}
